import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import SuppliersSubPage from './SuppliersSubPage';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { useAuthStore } from '@/store/auth.store';
import { LoadingSpinner, PageError, ConfirmDialog } from '@/components/ui';
import type { GoodsReception, Supplier, ReceptionCategory } from '@haccp/api-client';

const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const TruckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 17h1M15 17h1M3 11V7a1 1 0 011-1h9l3 4h3a1 1 0 011 1v4a1 1 0 01-1 1h-1M3 11h13M3 11v4a1 1 0 001 1h1m0 0a2 2 0 104 0m-4 0a2 2 0 104 0m6 0a2 2 0 104 0 2 2 0 00-4 0" />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CameraIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const PencilIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
  </svg>
);

const categoryColors: Record<ReceptionCategory, string> = {
  FRAIS: 'bg-blue-100 text-blue-700',
  SEC: 'bg-amber-100 text-amber-700',
  SURGELE: 'bg-cyan-100 text-cyan-700',
};

const needsTemperature = (cat: ReceptionCategory) => cat === 'FRAIS' || cat === 'SURGELE';

export default function ReceptionPage() {
  const { t, i18n } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);

  const categoryLabels: Record<ReceptionCategory, string> = {
    FRAIS: t('reception.categoryFrais'),
    SEC: t('reception.categorySec'),
    SURGELE: t('reception.categorySurgele'),
  };

  const [showForm, setShowForm] = useState(false);
  const [showSuppliers, setShowSuppliers] = useState(false);
  const [selectedReception, setSelectedReception] = useState<GoodsReception | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    supplierId: '',
    category: 'FRAIS' as ReceptionCategory,
    temperature: '',
    orderNumber: '',
    invoiceNumber: '',
    packagingOk: true,
  });
  const [formData, setFormData] = useState({
    supplierId: '',
    category: 'FRAIS' as ReceptionCategory,
    temperature: '',
    orderNumber: '',
    invoiceNumber: '',
    packagingOk: true,
  });
  const [showQuickSupplier, setShowQuickSupplier] = useState<'create' | 'edit' | null>(null);
  const [quickSupplierData, setQuickSupplierData] = useState({ name: '', phone: '', email: '' });

  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => apiClient.getSuppliers(),
  });

  const { data: receptionsData, isLoading, error: receptionsError } = useQuery({
    queryKey: ['receptions'],
    queryFn: () => apiClient.getReceptions(0, 100),
  });

  const createMutation = useMutation({
    mutationFn: (data: {
      supplierId: string;
      category: ReceptionCategory;
      temperature?: number;
      orderNumber?: string;
      invoiceNumber?: string;
      packagingOk?: boolean;
    }) => apiClient.createReception(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receptions'] });
      resetForm();
      addToast(t('reception.receptionSaved'), 'success');
    },
    onError: () => addToast(t('reception.saveError'), 'error'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteReception(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receptions'] });
      setSelectedReception(null);
      addToast(t('reception.receptionDeleted'), 'success');
    },
    onError: () => addToast(t('reception.deleteError'), 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: {
      supplierId?: string;
      category?: ReceptionCategory;
      temperature?: number;
      orderNumber?: string;
      invoiceNumber?: string;
      packagingOk?: boolean;
    }}) => apiClient.updateReception(id, data),
    onSuccess: (updated: GoodsReception) => {
      queryClient.invalidateQueries({ queryKey: ['receptions'] });
      setSelectedReception(updated);
      setIsEditing(false);
      addToast(t('reception.receptionUpdated'), 'success');
    },
    onError: () => addToast(t('reception.updateError'), 'error'),
  });

  const quickAddSupplierMutation = useMutation({
    mutationFn: (data: { name: string; phone?: string; email?: string }) =>
      apiClient.createSupplier(data),
    onSuccess: (newSupplier: Supplier) => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      if (showQuickSupplier === 'create') setFormData(d => ({ ...d, supplierId: newSupplier.id }));
      if (showQuickSupplier === 'edit') setEditData(d => ({ ...d, supplierId: newSupplier.id }));
      setShowQuickSupplier(null);
      setQuickSupplierData({ name: '', phone: '', email: '' });
      addToast(t('suppliers.added'), 'success');
    },
    onError: () => addToast(t('suppliers.addError'), 'error'),
  });

  const photoMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      return apiClient.addReceptionPhoto(id, fd);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receptions'] });
      addToast(t('reception.photoAdded'), 'success');
    },
    onError: () => addToast(t('reception.photoError'), 'error'),
  });

  const receptions: GoodsReception[] = receptionsData?.data ?? [];

  const resetForm = () => {
    setShowForm(false);
    setFormData({ supplierId: '', category: 'FRAIS', temperature: '', orderNumber: '', invoiceNumber: '', packagingOk: true });
  };

  const startEditing = (reception: GoodsReception) => {
    setEditData({
      supplierId: reception.supplierId || '',
      category: reception.category,
      temperature: reception.temperature != null ? String(reception.temperature) : '',
      orderNumber: reception.orderNumber || '',
      invoiceNumber: reception.invoiceNumber || '',
      packagingOk: reception.packagingOk ?? true,
    });
    setIsEditing(true);
  };

  const handleEditSave = () => {
    if (!selectedReception) return;
    if (!editData.supplierId) {
      addToast(t('reception.selectSupplier'), 'error');
      return;
    }
    updateMutation.mutate({
      id: selectedReception.id,
      data: {
        supplierId: editData.supplierId,
        category: editData.category,
        temperature: editData.temperature ? parseFloat(editData.temperature) : undefined,
        orderNumber: editData.orderNumber || undefined,
        invoiceNumber: editData.invoiceNumber || undefined,
        packagingOk: editData.packagingOk,
      },
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.supplierId) {
      addToast(t('reception.selectSupplier'), 'error');
      return;
    }
    createMutation.mutate({
      supplierId: formData.supplierId,
      category: formData.category,
      temperature: formData.temperature ? parseFloat(formData.temperature) : undefined,
      orderNumber: formData.orderNumber || undefined,
      invoiceNumber: formData.invoiceNumber || undefined,
      packagingOk: formData.packagingOk,
    });
  };

  const handlePhotoUpload = (receptionId: string) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) photoMutation.mutate({ id: receptionId, file });
    };
    input.click();
  };

  const canWrite = user?.role === 'OWNER' || user?.role === 'STAFF';

  const formatDate = (d: Date | string) => {
    const date = new Date(d);
    return date.toLocaleDateString(i18n.language, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-primary-100 rounded-lg">
              <TruckIcon className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('reception.title')}</h1>
              <p className="text-slate-600">{t('reception.subtitle')}</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            {canWrite && (
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl hover:shadow-lg transition font-semibold"
              >
                <PlusIcon className="w-5 h-5" />
                {t('reception.addReception')}
              </button>
            )}
            <button
              onClick={() => setShowSuppliers(true)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-50 text-primary-700 rounded-xl border border-primary-200 hover:bg-primary-100 transition font-semibold"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
              {t('reception.suppliers')}
            </button>
          </div>
        </div>
      </div>
  {/* Suppliers Subpage Modal */}
  {showSuppliers && createPortal(<SuppliersSubPage onClose={() => setShowSuppliers(false)} />, document.body)}

      {/* Create Form Modal */}
      {showForm && createPortal(
        <div className="fixed inset-0 bg-black/50 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 my-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-slate-900 mb-4">{t('reception.newReception')}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Supplier */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('reception.supplier')} *</label>
                <select
                  required
                  value={formData.supplierId}
                  onChange={(e) => setFormData(d => ({ ...d, supplierId: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">{t('reception.selectSupplierOption')}</option>
                  {suppliers.map((s: Supplier) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => { setShowQuickSupplier(showQuickSupplier === 'create' ? null : 'create'); setQuickSupplierData({ name: '', phone: '', email: '' }); }}
                  className="mt-1.5 text-sm text-primary-600 hover:text-primary-800 font-semibold flex items-center gap-1 transition-colors"
                >
                  <span className="text-base leading-none">+</span> {t('suppliers.addBtn')}
                </button>
                {showQuickSupplier === 'create' && (
                  <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                    <p className="text-xs font-semibold text-slate-600">{t('suppliers.newTitle')}</p>
                    <input
                      autoFocus
                      type="text"
                      required
                      value={quickSupplierData.name}
                      onChange={(e) => setQuickSupplierData(d => ({ ...d, name: e.target.value }))}
                      placeholder={t('suppliers.namePlaceholder')}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <input
                      type="text"
                      value={quickSupplierData.phone}
                      onChange={(e) => setQuickSupplierData(d => ({ ...d, phone: e.target.value }))}
                      placeholder={t('suppliers.phonePlaceholder')}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setShowQuickSupplier(null)}
                        className="flex-1 px-3 py-1.5 text-slate-600 bg-white border border-slate-200 rounded-lg text-sm font-semibold hover:bg-slate-100 transition"
                      >{t('common.cancel')}</button>
                      <button
                        type="button"
                        disabled={!quickSupplierData.name.trim() || quickAddSupplierMutation.isPending}
                        onClick={() => quickAddSupplierMutation.mutate({ name: quickSupplierData.name.trim(), phone: quickSupplierData.phone || undefined })}
                        className="flex-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 transition disabled:opacity-50"
                      >{quickAddSupplierMutation.isPending ? '…' : t('suppliers.save')}</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('reception.category')} *</label>
                <div className="grid grid-cols-3 gap-2">
                  {(['FRAIS', 'SEC', 'SURGELE'] as ReceptionCategory[]).map((cat) => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setFormData(d => ({ ...d, category: cat, temperature: needsTemperature(cat) ? d.temperature : '' }))}
                      className={`px-3 py-2 rounded-xl text-sm font-semibold transition border-2 ${
                        formData.category === cat
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      {categoryLabels[cat]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Temperature (conditional) */}
              {needsTemperature(formData.category) && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {t('reception.temperatureLabel')} *
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    required
                    value={formData.temperature}
                    onChange={(e) => setFormData(d => ({ ...d, temperature: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder={t('reception.temperaturePlaceholder')}
                  />
                </div>
              )}

              {/* Order number */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('reception.orderNumber')}</label>
                <input
                  type="text"
                  value={formData.orderNumber}
                  onChange={(e) => setFormData(d => ({ ...d, orderNumber: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('reception.orderNumberPlaceholder')}
                />
              </div>

              {/* Invoice number */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('reception.invoiceNumber')}</label>
                <input
                  type="text"
                  value={formData.invoiceNumber}
                  onChange={(e) => setFormData(d => ({ ...d, invoiceNumber: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder={t('reception.invoiceNumberPlaceholder')}
                />
              </div>

              {/* Packaging state */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">{t('reception.packagingState')}</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData(d => ({ ...d, packagingOk: true }))}
                    className={`px-3 py-2 rounded-xl text-sm font-semibold transition border-2 ${
                      formData.packagingOk
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    ✓ {t('reception.conform')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(d => ({ ...d, packagingOk: false }))}
                    className={`px-3 py-2 rounded-xl text-sm font-semibold transition border-2 ${
                      !formData.packagingOk
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    ✗ {t('reception.nonConform')}
                  </button>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 px-4 py-2 text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition font-semibold"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl hover:shadow-lg transition font-semibold disabled:opacity-50"
                >
                  {t('reception.save')}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Detail / Edit Modal */}
      {selectedReception && createPortal(
        <div className="fixed inset-0 bg-black/50 z-50 flex flex-col items-center justify-start sm:justify-center overflow-y-auto p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 my-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-900">
                {isEditing ? t('reception.editReception') : t('reception.receptionDetail')}
              </h2>
              <button onClick={() => { setSelectedReception(null); setIsEditing(false); }} className="text-slate-400 hover:text-slate-600">✕</button>
            </div>

            {isEditing ? (
              /* ── Edit Mode ── */
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('reception.supplier')} *</label>
                  <select
                    value={editData.supplierId}
                    onChange={(e) => setEditData(d => ({ ...d, supplierId: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">{t('reception.selectSupplierOption')}</option>
                    {suppliers.map((s: Supplier) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => { setShowQuickSupplier(showQuickSupplier === 'edit' ? null : 'edit'); setQuickSupplierData({ name: '', phone: '', email: '' }); }}
                    className="mt-1.5 text-sm text-primary-600 hover:text-primary-800 font-semibold flex items-center gap-1 transition-colors"
                  >
                    <span className="text-base leading-none">+</span> {t('suppliers.addBtn')}
                  </button>
                  {showQuickSupplier === 'edit' && (
                    <div className="mt-2 p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                      <p className="text-xs font-semibold text-slate-600">{t('suppliers.newTitle')}</p>
                      <input
                        autoFocus
                        type="text"
                        required
                        value={quickSupplierData.name}
                        onChange={(e) => setQuickSupplierData(d => ({ ...d, name: e.target.value }))}
                        placeholder={t('suppliers.namePlaceholder')}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        type="text"
                        value={quickSupplierData.phone}
                        onChange={(e) => setQuickSupplierData(d => ({ ...d, phone: e.target.value }))}
                        placeholder={t('suppliers.phonePlaceholder')}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setShowQuickSupplier(null)}
                          className="flex-1 px-3 py-1.5 text-slate-600 bg-white border border-slate-200 rounded-lg text-sm font-semibold hover:bg-slate-100 transition"
                        >{t('common.cancel')}</button>
                        <button
                          type="button"
                          disabled={!quickSupplierData.name.trim() || quickAddSupplierMutation.isPending}
                          onClick={() => quickAddSupplierMutation.mutate({ name: quickSupplierData.name.trim(), phone: quickSupplierData.phone || undefined })}
                          className="flex-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 transition disabled:opacity-50"
                        >{quickAddSupplierMutation.isPending ? '…' : t('suppliers.save')}</button>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('reception.category')} *</label>
                  <div className="grid grid-cols-3 gap-2">
                    {(['FRAIS', 'SEC', 'SURGELE'] as ReceptionCategory[]).map((cat) => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setEditData(d => ({ ...d, category: cat, temperature: needsTemperature(cat) ? d.temperature : '' }))}
                        className={`px-3 py-2 rounded-xl text-sm font-semibold transition border-2 ${
                          editData.category === cat
                            ? 'border-primary-500 bg-primary-50 text-primary-700'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        {categoryLabels[cat]}
                      </button>
                    ))}
                  </div>
                </div>

                {needsTemperature(editData.category) && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">{t('reception.temperatureEditLabel')}</label>
                    <input
                      type="number"
                      step="0.1"
                      required
                      value={editData.temperature}
                      onChange={(e) => setEditData(d => ({ ...d, temperature: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder={t('reception.temperaturePlaceholder')}
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('reception.orderNumber')}</label>
                  <input
                    type="text"
                    value={editData.orderNumber}
                    onChange={(e) => setEditData(d => ({ ...d, orderNumber: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('reception.invoiceNumber')}</label>
                  <input
                    type="text"
                    value={editData.invoiceNumber}
                    onChange={(e) => setEditData(d => ({ ...d, invoiceNumber: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{t('reception.packagingState')}</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setEditData(d => ({ ...d, packagingOk: true }))}
                      className={`px-3 py-2 rounded-xl text-sm font-semibold transition border-2 ${
                        editData.packagingOk
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      ✓ {t('reception.conform')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditData(d => ({ ...d, packagingOk: false }))}
                      className={`px-3 py-2 rounded-xl text-sm font-semibold transition border-2 ${
                        !editData.packagingOk
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      ✗ {t('reception.nonConform')}
                    </button>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsEditing(false)}
                    className="flex-1 px-4 py-2 text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition font-semibold"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleEditSave}
                    disabled={updateMutation.isPending}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl hover:shadow-lg transition font-semibold disabled:opacity-50"
                  >
                    {updateMutation.isPending ? t('reception.saving') : t('reception.save')}
                  </button>
                </div>
              </div>
            ) : (
              /* ── View Mode ── */
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('reception.supplier')}</span>
                  <span className="font-semibold">{selectedReception.supplier?.name || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('reception.category')}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${categoryColors[selectedReception.category]}`}>
                    {categoryLabels[selectedReception.category]}
                  </span>
                </div>
                {selectedReception.temperature != null && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('reception.temperature')}</span>
                    <span className="font-semibold">{selectedReception.temperature}°C</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('reception.orderNumberShort')}</span>
                  <span className="font-semibold">{selectedReception.orderNumber || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('reception.invoiceNumberShort')}</span>
                  <span className="font-semibold">{selectedReception.invoiceNumber || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('reception.packaging')}</span>
                  <span className={`font-semibold ${selectedReception.packagingOk ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedReception.packagingOk ? `✓ ${t('reception.conform')}` : `✗ ${t('reception.nonConform')}`}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('reception.date')}</span>
                  <span className="font-semibold">{formatDate(selectedReception.createdAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('reception.recordedBy')}</span>
                  <span className="font-semibold">{selectedReception.creator?.displayName || selectedReception.creator?.email || '—'}</span>
                </div>

                {/* Photos */}
                {selectedReception.photos && selectedReception.photos.length > 0 && (
                  <div className="pt-2">
                    <p className="text-slate-500 mb-2">{t('reception.photos')}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {selectedReception.photos.map((photo) => (
                        <img
                          key={photo.id}
                          src={photo.photoUrl.startsWith('http') ? photo.photoUrl : `${(window as any).__API_BASE || ''}${photo.photoUrl}`}
                          alt="Reception photo"
                          className="w-full h-20 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-3">
                  {canWrite && (
                    <button
                      onClick={() => startEditing(selectedReception)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs font-semibold hover:bg-amber-100 transition"
                    >
                      <PencilIcon className="w-4 h-4" /> {t('common.edit')}
                    </button>
                  )}
                  {canWrite && (
                    <button
                      onClick={() => handlePhotoUpload(selectedReception.id)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg text-xs font-semibold hover:bg-primary-100 transition"
                    >
                      <CameraIcon className="w-4 h-4" /> {t('reception.addPhoto')}
                    </button>
                  )}
                  {user?.role === 'OWNER' && (
                    <button
                      onClick={() => setConfirmDeleteId(selectedReception.id)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-semibold hover:bg-red-100 transition"
                    >
                      <TrashIcon className="w-4 h-4" /> {t('common.delete')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Receptions List */}
      {isLoading ? (
        <LoadingSpinner message={t('reception.loading')} />
      ) : receptionsError ? (
        <PageError message={t('reception.loadError')} />
      ) : receptions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl shadow-card">
          <TruckIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-lg">{t('reception.noReceptions')}</p>
          <p className="text-slate-400 text-sm mt-1">{t('reception.noReceptionsHint')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {receptions.map((reception: GoodsReception) => (
            <div
              key={reception.id}
              onClick={() => setSelectedReception(reception)}
              className="bg-white rounded-2xl shadow-card p-4 hover:shadow-lg transition cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-bold text-slate-900">{reception.supplier?.name || t('reception.unknownSupplier')}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${categoryColors[reception.category]}`}>
                        {categoryLabels[reception.category]}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${reception.packagingOk ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {reception.packagingOk ? t('reception.packagingOk') : t('reception.nonConform')}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      {reception.temperature != null && <span>{reception.temperature}°C</span>}
                      {reception.orderNumber && <span>{t('reception.cmdShort')}: {reception.orderNumber}</span>}
                      {reception.invoiceNumber && <span>{t('reception.factShort')}: {reception.invoiceNumber}</span>}
                      <span>{formatDate(reception.createdAt)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {reception.photos && reception.photos.length > 0 && (
                    <span className="text-xs text-slate-400">{reception.photos.length} 📷</span>
                  )}
                  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDeleteId}
        title={t('reception.confirmDeleteTitle')}
        message={t('reception.confirmDeleteMessage')}
        onConfirm={() => {
          if (confirmDeleteId) deleteMutation.mutate(confirmDeleteId);
          setConfirmDeleteId(null);
        }}
        onCancel={() => setConfirmDeleteId(null)}
      />
    </div>
  );
}
